'use client'

import { useEffect, useRef } from 'react'
import { failedOperationsQueue } from '@/lib/failed-operations-queue'
import { useToast } from '@/components/ui/toast'
import { createClient } from '@/lib/supabase/client'

/**
 * Component that automatically processes failed operations when the app loads
 * Place this in your root layout to ensure it runs on every page load
 */
export function FailedOperationsProcessor() {
  const { info, success } = useToast()
  const isVideoProcessing = useRef(false)

  useEffect(() => {
    const supabase = createClient()

    // 1. Process standard IndexedDB offline queue
    const processOfflineQueue = async () => {
      try {
        const count = await failedOperationsQueue.getQueuedOperationsCount()
        if (count > 0) {
          console.log(`🔄 Found ${count} queued operation(s) - processing...`)
          await failedOperationsQueue.processQueuedOperations()
        }
      } catch (error) {
        console.error('❌ Error processing failed operations queue:', error)
      }
    }

    // 2. Process failed remote videos
    const processFailedVideos = async () => {
      if (isVideoProcessing.current) return
      isVideoProcessing.current = true

      try {
        // Find entries that failed transcription
        const { data: failedEntries, error } = await supabase
          .from('entries')
          .select('id, title, source_url')
          .ilike('summary', '%Transcription timed out%')

        if (error) {
           console.error('Failed to query failed videos:', error)
           return
        }

        if (failedEntries && failedEntries.length > 0) {
          const viableEntries = failedEntries.filter((e: any) => e.source_url && (e.source_url.includes('supabase.co/storage') || e.source_url.includes('link-cloud')))
          const unrecoverableLocalEntries = failedEntries.filter((e: any) => !e.source_url || (!e.source_url.includes('supabase.co/storage') && !e.source_url.includes('link-cloud')))

          if (unrecoverableLocalEntries.length > 0) {
             console.log(`[Retry queue] Ignoring ${unrecoverableLocalEntries.length} local mobile files to allow the iOS/Android app to handle their own raw file recovery.`)
          }

          if (viableEntries.length > 0) {
            console.log(`🔄 Found ${viableEntries.length} viable cloud video transcriptions to retry`)
            info(`Retrying ${viableEntries.length} failed cloud video transcriptions in background...`)

            for (const entry of viableEntries) {
              try {
                 const res = await fetch('/api/videos/retry', {
                   method: 'POST',
                   headers: { 'Content-Type': 'application/json' },
                   body: JSON.stringify({ entry_id: entry.id })
                 })

                 if (res.ok) {
                   success(`Successfully recovered video: ${entry.title || 'Untitled'}`)
                 } else {
                   const errText = await res.text()
                   let errData: any = errText
                   try { errData = JSON.parse(errText) } catch (e) {}
                   console.error(`Failed to retry video ${entry.id} (Status ${res.status}):`, errData)
                 }
              } catch (err) {
                 console.error(`Network error retrying video ${entry.id}:`, err)
              }
            }
          }
        }
      } catch (err) {
         console.error('Fatal error in video processor:', err)
      } finally {
         isVideoProcessing.current = false
      }
    }

    // Run both queues
    processOfflineQueue()
    processFailedVideos()

    // Process queues when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Tab became visible - checking for queued operations...')
        processOfflineQueue()
        processFailedVideos()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [info, success])

  // This component doesn't render anything
  return null
}
